<template>
    <div
        v-if="isDisplayed"
        ref="blockRef"
        class="block"
        :class="[
            { 'no-transition': blockTransition },
            ...getBlockClasses(block),
        ]"
        :style="getBlockStyles(block)"
    >
        <Component
            :is="component"
            v-bind="blockBindings"
            :error="error"
            v-on="blockListeners"
            @update:model-value="handleModelValueUpdated"
            @click="handleClick"
            @item-chosen="handleItemChosen"
            @download-document="handleDownloadDocument"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, watch, ref, inject, shallowRef, type ComputedRef, onMounted, nextTick, toRef } from 'vue'
import type { BlockModelValue, BlockProps, ExtendedBlock } from './types'
import { omit } from 'ramda'
import { clone } from 'widget-components/utilities/clone'
import equals from 'fast-deep-equal/es6'
import { type getCustomPageBlockComponent } from 'widget-components/integrations/CustomPage/block-map'
import type { CustomCssVariables, DisplayDocumentPayload } from 'widget-components/types'
import useCustomCss from 'widget-components/composables/custom-css'
import useConditions from 'widget-components/composables/conditions/useConditions'
import { useEventBus } from '@vueuse/core'
import { emitBlockUpdate } from './utilities/emitBlockUpdate'
import { flattenBlocks } from 'widget-components/utilities/containers/flatten-blocks'
import { BLOCK_TYPES } from './consts'
import { isNestableBlock } from 'widget-components/utilities/containers/is-nestable-block'
import { getBlockClasses, getBlockStyles } from 'widget-components/integrations/CustomPage/utilities/getBlockStyles'

const blockRef = ref<HTMLElement | undefined>()

const props = defineProps<BlockProps>()

const emit = defineEmits([
    'update:modelValue',
    'update:block',
    'click:preview-block',
    'download-document',
])

const blockId = computed(() => props.block.id)

const { isDisplayed, isDisabled: isDisabledCondition } = useConditions(
    blockId,
    toRef(props, 'parentIndex'),
    toRef(props, 'parentId'),
)

const getCustomPageBlockComponentFn = inject<getCustomPageBlockComponent>('getCustomPageBlockComponent')
const handleDownloadDocument = inject<(resource: DisplayDocumentPayload, id: string) => void>('handleDocumentDownload', () => {})
const customPageBuilder = inject<boolean>('customPageBuilder', false)
const isLoading = inject<ComputedRef<boolean>>('isLoading')
const isDisabled = inject<ComputedRef<boolean>>('isDisabled', computed(() => false))
const blockTransition = ref(true)

const component = shallowRef(getCustomPageBlockComponentFn?.(props.block))

const customCssVariables = ref<CustomCssVariables>()

const isBlockLoading = computed(() => {
    if (
        props.block.type === 'ACTION_BUTTON' &&
        ['NEXT', 'CLOSE'].includes(props.block.buttonType as string)
    ) {
        return isLoading?.value
    }

    if (['SELECTION_LIST', 'NAVIGATION'].includes(props.block.type)) {
        return isLoading?.value
    }

    return false
})

const applyCustomCss = () => {
    if (blockRef.value) {
        const { applyCssVariables, clearCssVariables, getBlockCssVariables } = useCustomCss()

        customCssVariables.value = getBlockCssVariables(props.block)

        clearCssVariables(blockRef.value) // remove previous values to allow property removal
        applyCssVariables(customCssVariables.value, blockRef.value)
    }
}

onMounted(() => {
    applyCustomCss()
    setTimeout(() => {
        blockTransition.value = false
    }, 400)
})

watch(() => isDisplayed.value, (v) => {
    if (v) {
        // nextTick to have freshly rendered blockRef available
        nextTick(applyCustomCss)
    }
})

const handleModelValueUpdated = (val: BlockModelValue) => {
    emit('update:modelValue', val)
    emitBlockUpdate(props.block.blockPath, {
        id: props.block.id,
        error: undefined,
        currentValue: val,
    })
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleAction = inject<(a: Record<string, any>) => void>('handleAction', () => undefined)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleTracker = inject<(a: Record<string, any>) => void>('handleTracker', () => undefined)

const handleClick = () => {
    if (blockBindings.value.type === 'ACTION_BUTTON' && !blockBindings.value.disabled && !blockBindings.value.loading) {
        handleAction({
            id: props.block.id,
            type: props.block.type,
            nextTask: props.block.nextTask,
        })
        handleTracker({
            actionName: 'actionButtonClicked',
            actionType: props.block.buttonType,
            taskUuid: props.block.nextTaskId,
        })
    }
}

const handleItemChosen = (_item: unknown) => {
    const item = _item as ExtendedBlock
    if (!blockBindings.value.disabled && !blockBindings.value.loading) {
        handleAction({
            id: props.block.id,
            type: props.block.type,
            nextTask: item.nextTask,
        })
        handleTracker({
            actionName: 'selectionListItemSelected',
            actionType: props.block.buttonType,
            taskUuid: props.block.nextTaskId,
        })
    }
}

const blockBindings = computed(() => (omit(['style'], {
    ...props.block,
    ...(props.block.internalData?.bindings || {}),
    blockHoisted: props.blockHoisted,
    disabled: isLoading?.value || isDisabledCondition.value || isDisabled.value,
    loading: isBlockLoading.value,
    customCssVariables: customCssVariables.value,
    modelValue: props.modelValue,
})))

const blockListeners = computed(() => {
    const listeners = props.block.internalData?.listeners || {}

    return Object.keys(listeners).reduce((acc, key) => {
        acc[key] = (...args: unknown[]) => {
            listeners[key].apply(undefined, [emit, props.block, ...args])
        }
        return acc
    }, {} as Record<string, unknown>)
})

const error = computed(() => props.block.error)

if (customPageBuilder) {
    const blockType = computed(() => {
        return props.block.type
    })

    watch(() => [blockType.value], () => {
        component.value = getCustomPageBlockComponentFn?.(props.block)
    }, { deep: true })

    watch( // update styles in builder
        () => props.block.style,
        (v, prev) => {
            if (!equals(v, prev)) {
                applyCustomCss()
            }
        }, { deep: true, immediate: true },
    )
}

const RESET_CURRENT_VALUE = ''

const resetBlockValue = (block: ExtendedBlock) => {
    if (block.blockPath) {
        emitBlockUpdate(block.blockPath, {
            initialValue: clone(block.currentValue),
            currentValue: RESET_CURRENT_VALUE,
        })
    } else {
        useEventBus('block-unmounted-event').emit(block.id)
    }
}

const handleResetBlockValue = (block: ExtendedBlock) => {
    if (isNestableBlock(block)) {
        flattenBlocks(block.children as ExtendedBlock[])
            // filter form blocks that still have a non-empty value (it may have been reset already)
            .filter(b => b.internalData?.type === BLOCK_TYPES.FORM && b.currentValue !== RESET_CURRENT_VALUE)
            .forEach(resetBlockValue)
    } else {
        resetBlockValue(block)
    }
}

watch(isDisplayed, (val) => {
    if (val) return
    handleResetBlockValue(props.block)
})
</script>

<style lang="sass" scoped>
.block
    font-weight: var(--tl-font-weight, normal)
    flex: 1
    min-width: var(--tl-container-min-width, 0)

    // reset non-inherited CSS
    --tl-container-border-radius: var(--tl-wc-border-radius)
    --tl-container-bg-color: none
    --tl-container-box-shadow: none
    --tl-container-padding: none


    --tl-container-border-left: none
    --tl-container-border-top: none
    --tl-container-border-right: none
    --tl-container-border-bottom: none

    > *
        --tl-container-min-width: 0

    &.no-transition :deep(*)
        transition: none !important

.preview
    &:hover
        background-color: var(--color-tl-blue-tint-extralight)
</style>
